import { type DeliveryMethod } from '@alto/deliver_api/types/delivery_methods/v1/delivery_method';
import { BREAKPOINTS, COLORS, SPACING } from '@alto/design-library-tokens';
import {
  Body,
  Button,
  Card,
  Column,
  Description,
  H1,
  InputCheckbox,
  LgPadding,
  LgSpacing,
  XsSpacing,
  XxsPadding,
  getBrand,
} from '@alto/design-system';
import {
  ADD_TO_SHIPMENT_MUTATION_KEY,
  CheckoutSplitShipmentHeader,
  CourierTipCard,
  DeliveryAddressCard,
  OrderBundlingPaymentSummary,
  PaymentMethodCard,
  PaymentSummary,
  SchedulingCard,
  SharpsContainerCard,
  SignatureConfirmationCard,
  getOrderBundlingCheckoutOrigin,
  useCheckoutMailRequest,
  useCheckoutValidationErrors,
  useCheckoutViewed,
  useOrderBundlingOptions,
  useSaveOrderText,
  useSignatureConfirmation,
} from '@alto/features';
import { type CartItem } from '@alto/orders_api/types/v1/cart_item';
import React, { useState } from 'react';
import styled from 'styled-components';
import { updateOrder } from '~shared/actions/checkout';
import { usePrescriptionsInCart } from '~shared/features/cart/hooks/usePrescriptionsInCart';
import { useCartDeliveryMethods } from '~shared/features/cart/queries/useCartDeliveryMethods';
import { MAIL_REQUEST_CONFIRMED_ERROR_MESSAGE } from '~shared/features/checkout/constants';
import { getIsOrderBundling } from '~shared/features/checkout/selectors/getCheckoutFlow';
import { getOrder, getOrderAddressId } from '~shared/features/checkout/selectors/getOrder';
import { type CheckoutValidationErrors } from '~shared/features/checkout/types';
import { type AnalyticsProps as TipAnalyticsProps } from '~shared/features/courier-tips/AnalyticsProps';
import { PRICING_INFO } from '~shared/features/my-meds/constants';
import { useAnalytics } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { useOrderPricing, useQueryShipmentFeesAvailable } from '~shared/queries';
import { useIsMutating } from '~shared/react-query';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { CheckoutDeliverySummary } from './CheckoutDeliverySummary';
import { useGetItSoonerFlow } from './useGetItSoonerFlow';
import { ContentWrapper, Main } from '~web/components/Page';

const getCheckoutHeaderBackgroundColor = () => {
  const brand = getBrand();
  return brand === 'fuzehealth'
    ? COLORS.BACKGROUND_COLORS.PRIMARY_LIGHTEST
    : COLORS.BACKGROUND_COLORS.TERTIARY_LIGHTEST;
};

const headerBackgroundColor = getCheckoutHeaderBackgroundColor();

const CheckoutPageContainer = styled.div`
  min-height: 100vh;
  padding-bottom: ${SPACING.STATIC.XXL.rem};
  background-color: ${COLORS.BACKGROUND_COLORS.WHITE};
`;

const CheckoutHeaderSection = styled.div`
  width: 100%;
  background-color: ${headerBackgroundColor};
  padding: ${SPACING.STATIC.XXL.rem} 0;
`;

const CheckoutHeaderContent = styled.div`
  max-width: ${BREAKPOINTS.SM}px;
  margin: 0 auto;
  padding: 0 ${SPACING.STATIC.MD.rem};
`;

const CheckoutContentWrapper = styled.div`
  max-width: ${BREAKPOINTS.SM}px;
  margin: 0 auto;
  padding: 0 ${SPACING.STATIC.MD.rem};
  transform: translateY(-${SPACING.STATIC.LG.rem});
`;

type CheckoutProps = {
  readonly handleEditTipAmount: (tipAmount: number) => void;
  readonly handleSaveOrder: (hasValidationErrors: boolean, validationErrors: CheckoutValidationErrors) => void;
  readonly isEditingOrder: boolean;
  readonly isTippable: boolean;
  readonly items: CartItem[];
  readonly orderIndex: number;
  readonly shipmentID: number | null | undefined;
  readonly tipAmount: number;
  readonly tipAnalyticsProps: TipAnalyticsProps;
};

export const Checkout = ({
  handleEditTipAmount,
  handleSaveOrder,
  isEditingOrder,
  isTippable,
  items,
  orderIndex,
  shipmentID,
  tipAmount,
  tipAnalyticsProps,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}: CheckoutProps) => {
  const dispatch = useDispatchShared();
  const { trackEvent } = useAnalytics();
  const order = useSelectorShared(getOrder);
  const addressID = useSelectorShared(getOrderAddressId);
  const { deliveryMethods } = useCartDeliveryMethods();
  const deliveryMethod: DeliveryMethod | undefined = deliveryMethods[orderIndex];
  const isSplitShipment = deliveryMethods.length > 1;
  const isFirstShipment = orderIndex === 0;
  const isLastShipment = orderIndex === deliveryMethods.length - 1;
  const { hasValidationErrors, validationErrors } = useCheckoutValidationErrors();

  const { openGetItSoonerFlow } = useGetItSoonerFlow();
  const { isLoading: fetchShipmentFeesLoading } = useQueryShipmentFeesAvailable();
  const { isOrderPricingLoading, isPaymentMethodRequired, orderPricing } = useOrderPricing({
    shipmentID: shipmentID ? shipmentID : undefined,
  });
  const fetchDeliveryWindowsLoading = useSelectorShared((state) => state.ui.loading.fetchDeliveryWindowsLoading);
  const isSavingOrder = useSelectorShared((state) => state.ui.loading.saveOrderLoading);
  const isSaveOrderButtonDisabled =
    fetchDeliveryWindowsLoading || isOrderPricingLoading || fetchShipmentFeesLoading || isSavingOrder;
  const { hasInjectables, hasOnlyInjectableCompounds } = usePrescriptionsInCart();
  const orderBundlingOrigin = getOrderBundlingCheckoutOrigin({ isEditingOrder });
  const { earliestUpcomingShipment } = useOrderBundlingOptions({
    address_id: addressID,
    origin: orderBundlingOrigin,
  });
  const isOrderBundling = useSelectorShared(getIsOrderBundling);
  const isAddToShipmentPending = !!useIsMutating({ mutationKey: ADD_TO_SHIPMENT_MUTATION_KEY });
  const isLoading = isSavingOrder || isAddToShipmentPending;
  const hidePaymentMethod =
    !isPaymentMethodRequired && isOrderBundling && !earliestUpcomingShipment?.payment_method_needed;
  const saveOrderText = useSaveOrderText({ isLastShipment });
  const { homeToSignConfirmationRequired } = useSignatureConfirmation();
  // Mail Request Section
  const [mailRequestChecked, setMailRequestChecked] = useState<boolean>(order.mail_request_confirmed ?? false);
  const { mailRequestRequired, mailRequestRequiredMessage, showMailRequestConfirmedError } = useCheckoutMailRequest();

  useCheckoutViewed({
    shipmentID,
    items,
    isEditingOrder,
    isSplitShipment: deliveryMethods.length > 1,
    mailRequestRequired,
  });

  const isSelectAddressDisabled = (!isFirstShipment && isSplitShipment) || isSaveOrderButtonDisabled;
  const isSelectDateDisabled = (!isFirstShipment && isSplitShipment) || isSaveOrderButtonDisabled;

  return (
    <CheckoutPageContainer>
      <CheckoutHeaderSection>
        <CheckoutHeaderContent>
          <Column>
            <H1>{isEditingOrder ? 'Edit Order' : 'Checkout'}</H1>
            {isSplitShipment ? <CheckoutSplitShipmentHeader orderIndex={orderIndex} /> : null}
          </Column>
        </CheckoutHeaderContent>
      </CheckoutHeaderSection>
      <CheckoutContentWrapper>
        <ContentWrapper>
          {/* FYI this override is somewhat arbitrary - the sidebar containing checkout summary felt too skinny with the default 3/4 1/4 layout */}
          <Main wide>
            <DeliveryAddressCard disabled={isSelectAddressDisabled} />
            <SchedulingCard openGetItSoonerFlow={openGetItSoonerFlow} />
            {homeToSignConfirmationRequired ? <SignatureConfirmationCard /> : null}
            {hasInjectables && !hasOnlyInjectableCompounds && isLastShipment ? <SharpsContainerCard /> : null}
            {!isOrderBundling && isTippable ? (
              <>
                <CourierTipCard
                  analyticsProps={tipAnalyticsProps}
                  handleEditTipAmount={handleEditTipAmount}
                  tipAmount={tipAmount}
                />
              </>
            ) : null}
            {hidePaymentMethod ? null : (
              <PaymentMethodCard
                deliveryMethodMode={deliveryMethod?.mode}
                disabled={isSelectDateDisabled}
              />
            )}
            <CheckoutDeliverySummary />
            {isOrderBundling ? (
              <OrderBundlingPaymentSummary
                showPaymentBreakdown
                origin={orderBundlingOrigin}
              />
            ) : (
              <PaymentSummary
                showZeroTipLineItem={isTippable}
                origin="Checkout"
              />
            )}
            {mailRequestRequired && isLastShipment ? (
              <XxsPadding>
                <InputCheckbox
                  key="mail-request-checkbox"
                  label=""
                  checkPlacement="left"
                  name="Mail request required"
                  fullBleed
                  noHighlight
                  descriptions={<Description>{mailRequestRequiredMessage}</Description>}
                  value={mailRequestChecked}
                  onPress={() => {
                    const newMailRequestChecked = !mailRequestChecked;
                    setMailRequestChecked(newMailRequestChecked);
                    dispatch(updateOrder({ mail_request_confirmed: newMailRequestChecked }));
                    trackEvent({
                      event: EVENTS.CHECKOUT__MAIL_REQUEST_CONFIRMED,
                      params: { is_checked: newMailRequestChecked },
                    });
                  }}
                />
                {showMailRequestConfirmedError ? (
                  <>
                    <XsSpacing />
                    <Description color={COLORS.TEXT_COLORS.DANGER}>{MAIL_REQUEST_CONFIRMED_ERROR_MESSAGE}</Description>
                  </>
                ) : null}
              </XxsPadding>
            ) : null}
            <Button
              disabled={isSaveOrderButtonDisabled}
              loading={isLoading}
              testID="checkout-submit"
              onPress={() => {
                handleSaveOrder(hasValidationErrors, validationErrors);
              }}
            >
              {saveOrderText}
            </Button>
            {orderPricing?.price_unconfirmed ? (
              <>
                <LgSpacing />
                <Card title={PRICING_INFO.when_charged}>
                  <LgPadding topPadding={SPACING.STATIC.NONE}>
                    <Body>{PRICING_INFO.wont_be_charged_until}</Body>
                  </LgPadding>
                </Card>
              </>
            ) : null}
          </Main>
        </ContentWrapper>
      </CheckoutContentWrapper>
    </CheckoutPageContainer>
  );
};
